"use client";

import Link from "next/link";
import { useId, useState, type InputHTMLAttributes, type ReactNode } from "react";

const INPUT =
  "h-11 w-full rounded-lg border border-ink-300 bg-white px-3.5 text-base text-ink-950 shadow-xs outline-none transition placeholder:text-ink-400 focus:border-brand-500 focus:ring-3 focus:ring-brand-500/20 disabled:bg-ink-50";

export function AuthField({
  label,
  help,
  type = "text",
  ...input
}: {
  label: string;
  help?: string;
} & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  const [shown, setShown] = useState(false);
  const isPassword = type === "password";

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink-800">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={isPassword && shown ? "text" : type}
          aria-describedby={help ? `${id}-help` : undefined}
          className={isPassword ? `${INPUT} pr-16` : INPUT}
          {...input}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShown((v) => !v)}
            className="absolute inset-y-0 right-1.5 my-1.5 rounded-md px-2.5 text-xs font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900"
            aria-label={shown ? "Hide password" : "Show password"}
          >
            {shown ? "Hide" : "Show"}
          </button>
        )}
      </div>
      {help && (
        <p id={`${id}-help`} className="text-xs text-ink-500">
          {help}
        </p>
      )}
    </div>
  );
}

export function AuthError({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800"
    >
      {children}
    </p>
  );
}

export function AuthSubmit({ pending, label, pendingLabel }: { pending: boolean; label: string; pendingLabel: string }) {
  return (
    <button
      type="submit"
      disabled={pending}
      className="h-11 w-full rounded-lg bg-brand-600 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {pending ? pendingLabel : label}
    </button>
  );
}

export function AuthLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <Link href={href} className="font-medium text-brand-700 underline-offset-2 hover:underline">
      {children}
    </Link>
  );
}
